import Image from 'next/image'
import Button from '../Button'
import React from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import useUserPreferences from '@/hooks/useUserPreferences'
import { useCurrency } from '../DesktopHeader'

interface Props {
  product: any
  inputPackaging: any
  setInputPackaging?: any
  SubmitChange?: any
  close?: any
  isEdit?: any
  setPackageModal?: any
  handleSave?: any
  packagingPrice?: any
  markCompleted?: any
  updateProductData?:any
}

export default function PackagingSelection({
  product,
  setInputPackaging,
  isEdit,
  setPackageModal,
  handleSave,
  packagingPrice,
  markCompleted,
  updateProductData
}: Props) {
  const {t} = useTranslation('personalizeModal');
  const {locale} = useRouter();
  const [selectedPackaging, setSelectedPackaging] = React.useState(false)
  const [selectedPackagingId, setSelectedPackagingId] = React.useState(0)
  const preferences = useUserPreferences();
  const currencySymbol = useCurrency(preferences?.currencyCode || 'ILS');

  React.useEffect(() => {
    if (isEdit === '1') {
      if (product?.packaging?.name === 'Packaging') {
        setSelectedPackaging(true);
        setSelectedPackagingId(2);
        if (setInputPackaging) {
          setInputPackaging(true);
        }
      } else {
        setSelectedPackaging(false);
        setSelectedPackagingId(1);
        if (setInputPackaging) {
          setInputPackaging(false);
        }
      }
    }
  }, [isEdit, product?.packaging, setInputPackaging]);

  const handlePackagingSave = () => {
    const finalPackagingValue = selectedPackagingId === 2;
    
    // Update product data with selected packaging
    if (updateProductData) {
      updateProductData({packaging: finalPackagingValue});
    }
    
    // Mark option as completed
    if (markCompleted) {
      markCompleted('packaging', true);
    }
    
    // Save changes
    if (handleSave) {
      handleSave();
    }
    
    // Close modal if needed
    if (setPackageModal) {
      setPackageModal(false);
    }
  };

  const demoPackagings = [
    {
      id: 2,
      image: '/assets/svgImages/With Packaging.svg',
      name: `${t('withPackagingName')}`,
      description: `${t('withPackagingDescription')}`,
      price: packagingPrice === undefined ? 25 : packagingPrice
    },
    {
      id: 1,
      image: '/assets/svgImages/Without Packaging.svg',
      name: `${t('withoutPackagingName')}`,
      description: `${t('withoutPackagingDescription')}`,
      price: 0
    }
  ]
  return (
    <div className="personalization-content covers-selection items-center w-full xl:mx-auto xl:max-w-xl px-4" dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}>
      <h3 className={`block lg:hidden text-base font-bold ${locale === 'ar' ? 'text-right': 'text-left'}`}>{t('packagingSelectionHeading')}</h3>

      <div className="hidden lg:flex w-full items-center justify-between lg:mb-3">
        <h3 className="text-base font-bold">{t('packagingSelectionHeading')}</h3>
        <button
          className="bg-[#93A3B03D] text-xl p-2 rounded-md"
          onClick={() => setPackageModal(false)}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8.23742 7.00023L13.7439 1.88765C14.0857 1.57026 14.0857 1.05566 13.7439 0.738291C13.4021 0.420895 12.8479 0.420895 12.5061 0.738291L7.00022 5.8514L1.49438 0.738291C1.15257 0.420895 0.598385 0.420895 0.256602 0.738291C-0.0851811 1.05569 -0.0852084 1.57028 0.256602 1.88765L5.76302 7.00023L0.256602 12.1128C-0.0852084 12.4302 -0.0852084 12.9448 0.256602 13.2622C0.598412 13.5796 1.15259 13.5796 1.49438 13.2622L7.00022 8.14905L12.5061 13.2622C12.8479 13.5796 13.4021 13.5796 13.7438 13.2622C14.0857 12.9448 14.0857 12.4302 13.7438 12.1128L8.23742 7.00023Z" fill="black"/>
          </svg>
        </button>
      </div>

      <div className="covers-list w-full mt-2 flex flex-col mx-auto gap-2">
        {demoPackagings.map((item: any) => (
          <a
            href="#"
            key={item.id}
            id="packaging-item"
            onClick={(e) => {
              e.preventDefault()
              setSelectedPackagingId(item.id)
              if (item.id === 1) {
                setSelectedPackaging(false)
                setInputPackaging(false)
              } else {
                setSelectedPackaging(true)
                setInputPackaging(true)
              }
            }}
            className={`bg-white cover transition duration-200 mt-1 lg:mt-0 cursor-pointer border-2 flex flex-col p-4 rounded-lg lg:items-start lg:w-full ${
              selectedPackagingId == item.id ? 'border-primary' : ''
            } gap-1`}
          >
            <div className="cover-image relative bg-[#0CDFBF] h-[120px] lg:h-[132px] lg:w-full rounded-md overflow-hidden">
              <Image
                src={item.image}
                alt={item.name}
                fill
                unoptimized
                priority
                className={`w-full object-contain object-center`}
              />
            </div>
            <h4 className={`mt-4 text-base font-medium ${locale === 'ar' ? 'text-right': 'text-left'}`}>{item.name}</h4>
            <p className={`text-sm text-text-color font-normal ${locale === 'ar' ? 'text-right': 'text-left'}`}>{item.description}</p>
            <strong className={`price text-red text-2xl font-bold mt-1 ${locale === 'ar' ? 'text-right': 'text-left'}`}>{item.price}{currencySymbol.symbol}</strong>
          </a>
        ))}
      </div>

      <Button
        type="button"
        bg="primary"
        id="packaging-selection-continue-button"
        onClick={handlePackagingSave}
        classes={['py-3', 'w-full', 'mt-4', 'font-semibold', 'hidden', 'lg:block', 'lg:mt-6']}
      >
        {`${t('continue')}`}
      </Button>
    </div>
  )
}